import { api } from '@/utils/api';

/** Assign the selected contacts to a sequence (from the search selection bar). */
export const useSequenceAssignMutation = () => {
  const utils = api.useUtils();
  return api.sequence.assign.useMutation({
    onSuccess: () => {
      void utils.sequence.list.invalidate();
      // Assigning enrolls people → the per-node face counts on the sequence graph AND the People list change.
      void utils.sequence.faces.invalidate();
      void utils.sequence.people.invalidate();
    },
  });
};

/** Remove contacts from a sequence (deletes their enrollment) — used by the per-face remove control. */
export const useSequenceRemoveMutation = () => {
  const utils = api.useUtils();
  return api.sequence.remove.useMutation({
    onSuccess: () => {
      // Removing unenrolls people → the per-node face piles on the sequence graph AND the People list change.
      void utils.sequence.faces.invalidate();
      void utils.sequence.people.invalidate();
    },
  });
};

/** Restart contacts' journeys (re-seed at the entry node + kick off) — used by the per-face restart control. */
export const useSequenceRestartMutation = () => {
  const utils = api.useUtils();
  return api.sequence.restart.useMutation({
    onSuccess: () => {
      // Restarting moves people back to the entry node → the per-node face piles AND the People list change.
      void utils.sequence.faces.invalidate();
      void utils.sequence.people.invalidate();
    },
  });
};

/** Mark a parked call/reminder to-do done (advances the arm past the step) — used by the per-face
 * "Mark done" control on a contact resting at status.call_waiting / status.reminder_waiting. */
export const useSequenceCompleteTaskMutation = () => {
  const utils = api.useUtils();
  return api.sequence.completeTask.useMutation({
    onSuccess: () => {
      // Completing the task advances the person off the wait node → the per-node face piles AND the People
      // list change.
      void utils.sequence.faces.invalidate();
      void utils.sequence.people.invalidate();
    },
  });
};

/**
 * Edit a sequence's settings (name / steps / audience / filter / loader toggle). The detail query feeds
 * every view on the page and the list shows name + metadata, so both refresh.
 */
export const useSequenceUpdateMutation = () => {
  const utils = api.useUtils();
  return api.sequence.update.useMutation({
    onSuccess: async (_data, input) => {
      await Promise.all([
        utils.sequence.get.invalidate({ teamId: input.teamId, sequenceId: input.sequenceId }),
        utils.sequence.list.invalidate({ teamId: input.teamId }),
      ]);
    },
  });
};

/**
 * Pause / resume the sequence's auto-loader (POS-2454). Pausing stops NEW people arriving — imports and
 * runs — while everyone already enrolled keeps walking the sequence, so only the sequence's own row and
 * the loader panel's payload (which carries the pause) refresh.
 */
export const useSequenceSetAutoLoadPausedMutation = () => {
  const utils = api.useUtils();
  return api.sequence.setAutoLoadPaused.useMutation({
    onSuccess: async (_data, input) => {
      const key = { teamId: input.teamId, sequenceId: input.sequenceId };
      await Promise.all([utils.sequence.get.invalidate(key), utils.sequence.autoLoadSearchList.invalidate(key)]);
    },
  });
};
